import { Injectable } from '@angular/core';

/**
 * Sanitizes backend-supplied formatting HTML (descriptions, instructions and
 * other informational text) down to a strict, predictable whitelist.
 *
 * The frontend is the final enforcement layer: unsupported tags, attributes,
 * styles and URL protocols are removed silently regardless of what the backend
 * stores. The browser's DOMParser is used in `text/html` mode, which produces an
 * inert document (no scripts run, no resources load), and a brand new element
 * tree is rebuilt copying only explicitly allowed nodes so nothing from the
 * untrusted input is ever trusted by default.
 */
@Injectable({ providedIn: 'root' })
export class HtmlContentSanitizerService {
  private static readonly ALLOWED_TAGS = new Set<string>([
    'br',
    'b',
    'strong',
    'u',
    'p',
    'span',
    'mark',
    'ul',
    'ol',
    'li',
    'a',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
  ]);

  // Tags that must be dropped together with their entire subtree (never keep
  // their text content), because the content itself is unsafe or meaningless.
  private static readonly DROP_WITH_CONTENT_TAGS = new Set<string>([
    'script',
    'style',
    'iframe',
    'object',
    'embed',
    'form',
    'input',
    'button',
    'link',
    'img',
    'svg',
    'video',
    'audio',
    'textarea',
    'select',
    'option',
    'noscript',
    'template',
  ]);

  private static readonly ALLOWED_CSS_PROPERTIES = new Set<string>([
    'color',
    'background-color',
  ]);

  // Only absolute https:// links and mailto: addresses are permitted.
  private static readonly SAFE_URL_PATTERN = /^(?:https:\/\/|mailto:)/i;

  // Whitelisted color value shapes. A positive allowlist (rather than blacklisting
  // dangerous substrings) is used on purpose: CSS accepts escape sequences,
  // comments and other obfuscation (e.g. "\75 rl(" resolves to "url("), so any
  // blacklist of literal tokens can be bypassed. These patterns only accept a
  // tightly bounded character set and never allow a backslash or "(" outside the
  // known color functions, which makes injection like url(...)/expression(...)
  // impossible to express.
  private static readonly SAFE_COLOR_VALUE_PATTERNS: readonly RegExp[] = [
    // Named colors and bare keywords (transparent, currentcolor, inherit, ...).
    /^[a-z]+$/,
    // Hex colors: #rgb, #rgba, #rrggbb, #rrggbbaa.
    /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/,
    // Functional color notations with only numeric/percent/separator characters.
    /^(?:rgb|rgba|hsl|hsla)\([0-9.,%/\s]+\)$/,
  ];

  /**
   * Normalizes newline sequences into <br>, then returns sanitized HTML safe to
   * render. Returns an empty string for nullish/empty input.
   */
  sanitize(rawHtml: string | null | undefined): string {
    if (!rawHtml) {
      return '';
    }

    const normalized = this.normalizeNewlines(rawHtml);
    const parsed = new DOMParser().parseFromString(normalized, 'text/html');
    const cleanRoot = parsed.createElement('div');
    this.appendSanitizedChildren(parsed.body, cleanRoot, parsed);
    return cleanRoot.innerHTML;
  }

  /**
   * Converts escaped and literal newline sequences into <br> so admins can write
   * readable multi-line content without manually adding tags. Runs before
   * sanitization so the resulting <br> tags pass through the whitelist.
   */
  private normalizeNewlines(text: string): string {
    return text
      .replace(/\\r\\n/g, '\n')
      .replace(/\\r/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\n/g, '<br>');
  }

  private appendSanitizedChildren(
    source: Node,
    target: Element,
    doc: Document,
  ): void {
    source.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        target.appendChild(doc.createTextNode(node.textContent ?? ''));
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        // Comments, processing instructions, etc. are dropped.
        return;
      }

      const element = node as Element;
      const tag = element.tagName.toLowerCase();

      if (HtmlContentSanitizerService.ALLOWED_TAGS.has(tag)) {
        const cleanElement = doc.createElement(tag);
        this.applyAllowedAttributes(tag, element, cleanElement);
        this.appendSanitizedChildren(element, cleanElement, doc);
        target.appendChild(cleanElement);
        return;
      }

      if (HtmlContentSanitizerService.DROP_WITH_CONTENT_TAGS.has(tag)) {
        // Drop the element and everything inside it.
        return;
      }

      // Unknown but non-dangerous tag: unwrap it and keep its sanitized
      // children so readable text content is preserved.
      this.appendSanitizedChildren(element, target, doc);
    });
  }

  private applyAllowedAttributes(
    tag: string,
    source: Element,
    target: Element,
  ): void {
    if (tag === 'span') {
      const safeStyle = this.sanitizeStyle(source.getAttribute('style'));
      if (safeStyle) {
        target.setAttribute('style', safeStyle);
      }
      return;
    }

    if (tag === 'a') {
      const href = this.sanitizeHref(source.getAttribute('href'));
      if (href) {
        target.setAttribute('href', href);
        // Required security rules: every rendered link opens isolated.
        target.setAttribute('target', '_blank');
        target.setAttribute('rel', 'noopener noreferrer');
      }
      return;
    }

    // All other allowed tags carry no attributes.
  }

  /**
   * Keeps only the color and background-color declarations and rejects any value
   * that could smuggle CSS injection (urls, expressions, scripts).
   */
  private sanitizeStyle(style: string | null): string {
    if (!style) {
      return '';
    }

    return style
      .split(';')
      .map((declaration) => declaration.trim())
      .filter(Boolean)
      .reduce<string[]>((accepted, declaration) => {
        const separatorIndex = declaration.indexOf(':');
        if (separatorIndex === -1) {
          return accepted;
        }

        const property = declaration
          .slice(0, separatorIndex)
          .trim()
          .toLowerCase();
        const value = declaration.slice(separatorIndex + 1).trim();

        if (!HtmlContentSanitizerService.ALLOWED_CSS_PROPERTIES.has(property)) {
          return accepted;
        }

        if (!this.isSafeColorValue(value)) {
          return accepted;
        }

        accepted.push(`${property}: ${value}`);
        return accepted;
      }, [])
      .join('; ');
  }

  /**
   * Validates a CSS value against a strict allowlist of safe color notations.
   *
   * Uses a positive allowlist instead of blacklisting dangerous tokens: CSS
   * accepts escape sequences (e.g. "\75 rl(" -> "url("), comments and other
   * obfuscation, so a literal-substring blacklist can be bypassed. A backslash
   * (the gateway to escape sequences) is rejected outright before pattern
   * matching, and the patterns themselves only permit a bounded character set.
   */
  private isSafeColorValue(value: string): boolean {
    if (!value || value.includes('\\')) {
      return false;
    }

    const normalized = value.trim().toLowerCase();
    return HtmlContentSanitizerService.SAFE_COLOR_VALUE_PATTERNS.some(
      (pattern) => pattern.test(normalized),
    );
  }

  /**
   * Allows only absolute https:// and mailto: links. Control characters are
   * stripped first so protocols cannot be obfuscated (e.g. "java\tscript:").
   */
  private sanitizeHref(href: string | null): string {
    if (!href) {
      return '';
    }

    const cleaned = href.replace(/[\u0000-\u001F\u007F]/g, '').trim();
    return HtmlContentSanitizerService.SAFE_URL_PATTERN.test(cleaned)
      ? cleaned
      : '';
  }
}
